import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from './ToastContext';
import { useCustomer } from './CustomerContext';
import wishlistService from '../services/wishlistService';

const WishlistContext = createContext(undefined);

export const WishlistProvider = ({ children }) => {
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const { customer } = useCustomer(); // To check if user is logged in

    const fetchWishlist = async () => {
        if (!customer) {
            setWishlist([]);
            return;
        }
        try {
            setLoading(true);
            const response = await wishlistService.getWishlist();
            if (response.data.success && response.data.data && response.data.data.wishlist) {
                // Map backend items to frontend structure
                const mappedItems = response.data.data.wishlist.items.map(item => ({
                    id: item._id, // Wishlist Item ID (for removal)
                    itemId: item.itemId, // Actual Product ID
                    name: item.itemName,
                    price: item.basePrice,
                    image: item.imageUrl,
                    itemType: item.itemType,
                    addedAt: item.addedAt
                }));
                setWishlist(mappedItems);
            } else {
                setWishlist([]);
            }
        } catch (error) {
            console.error('Failed to fetch wishlist', error);
            // Optionally show toast error, but usually silent fail for fetching is better UX on init
        } finally {
            setLoading(false);
        }
    };

    // Load wishlist on mount or when customer changes
    useEffect(() => {
        if (customer) {
            fetchWishlist();
        } else {
            setWishlist([]);
        }
    }, [customer]);

    const addToWishlist = async (item) => {
        if (!customer) {
            showToast('Please login to add to wishlist', 'info');
            return;
        }

        // Optimistic update check
        if (wishlist.some(i => i.itemId === item.id)) {
            showToast(`${item.name} is already in your wishlist`, 'info');
            return;
        }

        try {
            const payload = {
                itemId: item.id,
                itemType: item.itemType || 'FoodItemMaster' // Default to FoodItem if not specified
            };

            const response = await wishlistService.addToWishlist(payload);

            if (response.data.success) {
                showToast(`${item.name} added to wishlist`, 'success', item.image);
                // Refresh wishlist to get the correct structure including _id
                fetchWishlist();
            }
        } catch (error) {
            console.error('Failed to add to wishlist', error);
            const msg = error.response?.data?.message || 'Failed to add to wishlist';
            showToast(msg, 'error');
        }
    };

    const removeFromWishlist = async (id) => {
        if (!customer) return;

        // Optimistic update
        const previousWishlist = [...wishlist];
        setWishlist((prev) => prev.filter((i) => i.id !== id));

        try {
            const response = await wishlistService.removeFromWishlist(id);
            if (response.data.success) {
                const item = previousWishlist.find(i => i.id === id);
                if (item) {
                    showToast(`${item.name} removed from wishlist`, 'info', item.image);
                }
            } else {
                // Revert on failure
                setWishlist(previousWishlist);
                showToast('Failed to remove item', 'error');
            }
        } catch (error) {
            console.error('Failed to remove from wishlist', error);
            setWishlist(previousWishlist);
            showToast('Failed to remove from wishlist', 'error');
        }
    };

    const isInWishlist = (itemId) => wishlist.some((i) => i.itemId === itemId);

    const toggleWishlist = (item) => {
        // Find if item is in wishlist (check by actual Item ID, not Wishlist Item ID)
        const wishlistItem = wishlist.find(i => i.itemId === item.id);

        if (wishlistItem) {
            // Remove using the Wishlist Item ID
            removeFromWishlist(wishlistItem.id);
        } else {
            addToWishlist(item);
        }
    };

    return (
        <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, isInWishlist, toggleWishlist, loading }}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlist = () => {
    const context = useContext(WishlistContext);
    if (!context) throw new Error('useWishlist must be used within WishlistProvider');
    return context;
};
